import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getCervicalCancerScreeningSummary } from '../HedisMetricService';
import CervicalCancerScreeningSummary from '../../../Models/HedisMetrics/CervicalCancerScreeningSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import { IconButton, Grid, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const CervicalCancerScreeningSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [cervicalCancerScreeningSummary, setCervicalCancerScreeningSummary] = useState<CervicalCancerScreeningSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        cervicalCancerScreeningHealthPlans,
        setCervicalCancerScreeningHealthPlans,
        cervicalCancerScreeningTeam,
        setCervicalCancerScreeningTeam,
        cervicalCancerScreeningDateFilter,
        setCervicalCancerScreeningDateFilter,
        cervicalCancerScreeningFromDate,
        setCervicalCancerScreeningFromDate,
        cervicalCancerScreeningToDate,
        setCervicalCancerScreeningToDate,
        cervicalCancerScreeningIncludePastOpenStatus,
        setCervicalCancerScreeningIncludePastOpenStatus,
        cervicalCancerScreeningShowInActiveTeam,
        setCervicalCancerScreeningShowInActiveTeam,
        setHedisType,
        setHealthPlans,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend(
                    'Cervical Cancer Screening',
                    'Screened vs Not Screened',
                    '',
                    'Count',
                    [{ name: '', data: [0] }],
                    [''],
                    null,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (
            cervicalCancerScreeningDateFilter !== TimePeriodFilterEnum.Custom ||
            (Boolean(cervicalCancerScreeningFromDate) && Boolean(cervicalCancerScreeningToDate))
        ) {
            const changeFilter = async () => {
                let start = cervicalCancerScreeningFromDate !== null ? new Date(cervicalCancerScreeningFromDate).toDateString() : null;
                let end = cervicalCancerScreeningToDate !== null ? new Date(cervicalCancerScreeningToDate).toDateString() : null;

                const data = await getCervicalCancerScreeningSummary(
                    cervicalCancerScreeningHealthPlans,
                    cervicalCancerScreeningTeam,
                    cervicalCancerScreeningDateFilter,
                    start,
                    end,
                    cervicalCancerScreeningIncludePastOpenStatus,
                    cervicalCancerScreeningShowInActiveTeam
                );
                setCervicalCancerScreeningSummary(data);
                setIsPrepared(true);
            };
            if (
                (cervicalCancerScreeningFromDate !== null || cervicalCancerScreeningToDate !== null) &&
                cervicalCancerScreeningToDate < cervicalCancerScreeningFromDate
            ) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Cervical Cancer Screening',
                        'Screened vs Not Screened',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cervicalCancerScreeningHealthPlans,
        cervicalCancerScreeningTeam,
        cervicalCancerScreeningDateFilter,
        cervicalCancerScreeningFromDate,
        cervicalCancerScreeningToDate,
        cervicalCancerScreeningIncludePastOpenStatus,
        cervicalCancerScreeningShowInActiveTeam,
    ]);

    useEffect(() => {
        if (cervicalCancerScreeningSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: cervicalCancerScreeningSummary.Screened,
                name: 'Screened',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Screened', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: cervicalCancerScreeningSummary.NotScreened,
                name: 'Not Screened',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Screened', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            xAxisValues.push(cervicalCancerScreeningHealthPlans);

            if (cervicalCancerScreeningSummary.Screened === 0 && cervicalCancerScreeningSummary.NotScreened === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Cervical Cancer Screening',
                        'Screened vs Not Screened',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(configureStackedBarChartWithoutLegend('Cervical Cancer Screening', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cervicalCancerScreeningSummary, cervicalCancerScreeningHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlans(cervicalCancerScreeningHealthPlans);
        setTeam(cervicalCancerScreeningTeam);
        setFilter(cervicalCancerScreeningDateFilter);
        setFDate(cervicalCancerScreeningFromDate);
        setTDate(cervicalCancerScreeningToDate);
        setIncludePastOpenStatus(cervicalCancerScreeningIncludePastOpenStatus);
        setShowInActiveTeam(cervicalCancerScreeningShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.CervicalCancerScreening);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/CervicalCancerScreening`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            Cervical Cancer Screening
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">
                                        For females 21-64 years of age, if one of the following criteria is met, the cancer screening metric is considered to be
                                        met
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Screened :
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        Last Met based on the external claim source in the last 2 years
                                    </Typography>
                                    <Typography variant="caption">
                                        Member's age 21-64 with any of the CPT codes - 88141, 88142, 88143, 88147, 88148, 88150, 88152, 88153, 88164, 88165,
                                        88166, 88167, 88174, 88175, or HCPCS G0123, G0124, G0141, G0143, G0144, G0145, G0147, G0148, P3000, P3001, Q0091 within
                                        the last 3 years
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        Member's age 30-64 with any of the CPT codes - 87624, 87625, 88141, 88142, 88143, 88147, 88148, 88150, 88152, 88153,
                                        88164, 88165, 88166, 88167, 88174, 88175, or HCPCS G0123, G0124, G0141, G0143, G0144, G0145, G0147, G0148, G0476, P3000,
                                        P3001, Q0091 within the last 5 years
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Screened :
                                    </Typography>
                                    <Typography variant="caption">No CPT codes recorded</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Exclusions :
                                    </Typography>
                                    <Typography variant="caption">Female age 65 or older</Typography>
                                    <Typography variant="caption" display="block">
                                        CPT: 57530, 57531, 57540, 57545, 57550, 57555, 57556, 58150, 58152, 58200, 58210, 58240, 58260, 58262, 58263, 58267,
                                        58270, 58275, 58280, 58285, 58290, 58291, 58292, 58293, 58294, 58548, 58550, 58552, 58553, 58554, 58570, 58571, 58572,
                                        58573, 58575, 58951, 58953, 58954, 58956, 59135, 99377, 99378
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        HCPCS: G0182, G9473-G9479, Q5003-Q5008, Q5010, S9126, T2042-T2046, G9054, M1017
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        ICD10: Q51.5, Z90.710, Z90.712, 0UTC0ZZ, 0UTC4ZZ, 0UTC7ZZ, 0UTC8ZZ
                                    </Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={cervicalCancerScreeningTeam}
                        setSelectedTeam={setCervicalCancerScreeningTeam}
                        dateFilter={cervicalCancerScreeningDateFilter}
                        setDateFilter={setCervicalCancerScreeningDateFilter}
                        fromDate={cervicalCancerScreeningFromDate}
                        setFromDate={setCervicalCancerScreeningFromDate}
                        toDate={cervicalCancerScreeningToDate}
                        setToDate={setCervicalCancerScreeningToDate}
                        healthPlan={cervicalCancerScreeningHealthPlans}
                        setHealthPlan={setCervicalCancerScreeningHealthPlans}
                        includePastOpenStatusMembers={cervicalCancerScreeningIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setCervicalCancerScreeningIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.CervicalCancerScreening}
                        showInActiveTeams={cervicalCancerScreeningShowInActiveTeam}
                        setShowInActiveTeams={setCervicalCancerScreeningShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default CervicalCancerScreeningSummaries;
